import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronsUpDown, Loader2, Plus } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/**
 * Searchable single-select picker bound to one of the centralized entity libraries.
 *
 * props:
 *   value          — the selected entity slug (or id) string
 *   onChange(slug, entity) — fires on select
 *   loader({ q }) → Promise<Entity[]>  — caller supplies the library loader
 *   getLabel(entity) — display formatter (default `name`)
 *   placeholder, testid
 */
export const EntityPicker = ({
  value,
  onChange,
  loader,
  getLabel = (e) => e.name,
  getSecondary = (e) => e.partner_type || e.scope || e.symbol || '',
  placeholder = 'Pick an entity…',
  testid = 'entity-picker',
}) => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const loaderRef = useRef(loader);
  useEffect(() => { loaderRef.current = loader; }, [loader]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const data = await loaderRef.current({ q: q || undefined });
        if (!cancelled) setItems(data || []);
      } catch (_) {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [open, q]);

  const selected = useMemo(() => items.find((i) => i.slug === value || i.id === value), [items, value]);
  const display = selected ? getLabel(selected) : (value || '');

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" className="w-full justify-between h-9 font-normal" data-testid={`${testid}-trigger`}>
          <span className={cn('truncate text-xs', !value && 'text-muted-foreground')}>{display || placeholder}</span>
          <ChevronsUpDown className="h-3.5 w-3.5 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput value={q} onValueChange={setQ} placeholder="Search…" data-testid={`${testid}-search`} />
          <CommandList>
            {loading ? (
              <div className="p-3 text-center text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin mx-auto" /></div>
            ) : items.length === 0 ? (
              <CommandEmpty data-testid={`${testid}-empty`}>No matches.</CommandEmpty>
            ) : (
              <CommandGroup>
                {items.map((it) => (
                  <CommandItem
                    key={it.id}
                    value={it.slug || it.id}
                    onSelect={() => { onChange?.(it.slug || it.id, it); setOpen(false); }}
                    className="text-xs"
                    data-testid={`${testid}-item-${it.slug || it.id}`}
                  >
                    <Check className={cn('h-3 w-3 mr-2', (selected && (selected.id === it.id || selected.slug === it.slug)) ? 'opacity-100' : 'opacity-0')} />
                    <div className="flex-1 min-w-0">
                      <div className="truncate">{getLabel(it)}</div>
                      {getSecondary(it) && <div className="text-[10px] text-muted-foreground truncate">{getSecondary(it)}</div>}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
